import { useIntl } from 'react-intl';
import { ButtonUI, Checkbox, Range } from 'sonata-react-components';

import { ViewToggle } from '@/components/ViewToggle/ViewToggle';

import type { ReactElement } from 'react';
import type { MetricFilters, MetricsUnitMode } from '@/services/footfall.service';

import './poiFiltersSidebar.scss';

export type MetricKey = 'baseline' | 'cpsv' | 'uplift';

export type MetricBounds = { min: number; max: number };

const METRIC_ORDER: MetricKey[] = ['baseline', 'uplift', 'cpsv'];

export type BrandOption = { brand: string; count: number };

type Props = {
  metricFilters: MetricFilters;
  onMetricFiltersChange: (metricFilters: MetricFilters) => void;
  unitMode: MetricsUnitMode;
  onUnitModeChange: (unitMode: MetricsUnitMode) => void;
  brandOptions: BrandOption[];
  brandFilter: string[];
  onBrandFilterChange: (brandFilter: string[]) => void;
  metricBounds: Record<MetricKey, MetricBounds>;
};

type MetricRangeInputProps = {
  id: MetricKey;
  label: string;
  suffix?: string;
  min: number;
  max: number;
  onRangeChange: (id: MetricKey, value: [number, number]) => void;
  value: [number, number];
};

function MetricRangeInput({
  id,
  label,
  suffix,
  min,
  max,
  onRangeChange,
  value
}: Readonly<MetricRangeInputProps>): ReactElement {
  const { formatMessage } = useIntl();
  const average = (value[0] + value[1]) / 2;

  return (
    <Range
      id={`poiFiltersSidebar-metric-${id}`}
      isEqualityAllowed
      max={max}
      min={min}
      onChange={rangeValue =>
        onRangeChange(id, (Array.isArray(rangeValue) ? rangeValue : [rangeValue]) as [number, number])
      }
      type="range"
      value={value}
    >
      <Range.Box title={label}>
        <Range.Input decimalDigits={2} />
        <Range.Bar step={0.01} />
        <Range.Info>
          <Range.Info.Left>{`${min.toFixed(2)}${suffix ?? ''}`}</Range.Info.Left>
          <Range.Info.Center>{`${formatMessage({
            defaultMessage: 'Avg',
            id: 'driveToStore.poiSelectionModal.filtersSidebar.average'
          })}: ${average.toFixed(2)}${suffix ?? ''}`}</Range.Info.Center>
          <Range.Info.Right>{`${max.toFixed(2)}${suffix ?? ''}`}</Range.Info.Right>
        </Range.Info>
      </Range.Box>
    </Range>
  );
}

export function PoiFiltersSidebar({
  metricFilters,
  onMetricFiltersChange,
  unitMode,
  onUnitModeChange,
  brandOptions,
  brandFilter,
  onBrandFilterChange,
  metricBounds
}: Readonly<Props>) {
  const { formatMessage } = useIntl();

  const metricLabels: Record<MetricKey, string> = {
    baseline: formatMessage({
      defaultMessage: 'Baseline',
      id: 'driveToStore.poiSelectionModal.advancedFilters.baseline'
    }),
    cpsv: formatMessage({ defaultMessage: 'CPSV', id: 'driveToStore.poiSelectionModal.advancedFilters.cpsv' }),
    uplift: formatMessage({ defaultMessage: 'Uplift', id: 'driveToStore.poiSelectionModal.advancedFilters.uplift' })
  };

  const rangeValue = (key: MetricKey): [number, number] => {
    const { min, max } = metricBounds[key];
    return [metricFilters[key]?.min ?? min, metricFilters[key]?.max ?? max];
  };

  const handleRangeChange = (id: MetricKey, [selectedMin, selectedMax]: [number, number]) => {
    const { min, max } = metricBounds[id];
    const next: MetricFilters = { ...metricFilters };
    if (selectedMin === min && selectedMax === max) {
      delete next[id];
    } else {
      next[id] = { max: selectedMax, min: selectedMin };
    }
    onMetricFiltersChange(next);
  };

  const handleBrandToggle = (brand: string) => {
    const next = brandFilter.includes(brand) ? brandFilter.filter(b => b !== brand) : [...brandFilter, brand];
    onBrandFilterChange(next);
  };

  const handleResetAll = () => {
    onMetricFiltersChange({});
    onBrandFilterChange([]);
  };

  const handleUnitModeChange = (nextUnitMode: MetricsUnitMode) => {
    onUnitModeChange(nextUnitMode);
    onMetricFiltersChange({});
    onBrandFilterChange([]);
  };

  const visibleMetrics =
    unitMode === 'percentage' ? METRIC_ORDER.filter(key => key !== 'baseline' && key !== 'cpsv') : METRIC_ORDER;

  return (
    <div className="poiFiltersSidebar">
      <div className="poiFiltersSidebar__topRow">
        <ButtonUI
          icon="fa-sharp fa-solid fa-arrows-rotate"
          id="poiFiltersSidebar-resetAll"
          onClick={handleResetAll}
          size="ultraSmall"
          title={formatMessage({
            defaultMessage: 'Reset all filters',
            id: 'driveToStore.poiSelectionModal.filtersSidebar.resetAll'
          })}
          type="tertiary"
        />
        <ViewToggle<MetricsUnitMode>
          onChange={handleUnitModeChange}
          options={[
            {
              icon: 'fa-solid fa-percent',
              label: formatMessage({
                defaultMessage: 'Percentage',
                id: 'driveToStore.poiSelectionModal.filtersSidebar.percentage'
              }),
              value: 'percentage'
            },
            {
              icon: 'fa-solid fa-list-ol',
              label: formatMessage({
                defaultMessage: 'Values',
                id: 'driveToStore.poiSelectionModal.filtersSidebar.values'
              }),
              value: 'values'
            }
          ]}
          value={unitMode}
          variant="interaction"
        />
      </div>
      <hr className="poiFiltersSidebar__divider" />
      <div className="poiFiltersSidebar__ranges">
        {visibleMetrics.map(key => {
          const { min, max } = metricBounds[key];
          return (
            <MetricRangeInput
              id={key}
              key={`${key}-${unitMode}-${min}-${max}`}
              label={metricLabels[key]}
              max={max}
              min={min}
              onRangeChange={handleRangeChange}
              suffix={key === 'uplift' && unitMode === 'percentage' ? '%' : undefined}
              value={rangeValue(key)}
            />
          );
        })}
      </div>
      {brandOptions.length > 0 && (
        <div className="poiFiltersSidebar__brandList">
          <p className="poiFiltersSidebar__brandListTitle">
            {formatMessage({ defaultMessage: 'Brands', id: 'driveToStore.poiSelectionModal.filtersSidebar.brands' })}
          </p>
          {brandOptions.map(({ brand, count }) => (
            <Checkbox
              active={brandFilter.includes(brand)}
              id={`poiFiltersSidebar-brand-${brand}`}
              key={brand}
              labelText={`${brand} (${count})`}
              onClickAction={() => handleBrandToggle(brand)}
              tiny
              type="checkbox"
            />
          ))}
        </div>
      )}
    </div>
  );
}
